import { el, list, setAttr } from 'redom';

import ScrollObserver from 'scroll-observer';

import {
  appendMessage,
  prependMessages,
  isMessageExistsById,
  isExternalMessageExistsById,
  setMessageAsDelivered,
  setMessageAsViewed,
  removeMessage,
  updateMessage,
  findMessageByExternalId,
  findMessageById,
  buildMessageList
} from './utilities';

import MessageGroup from './message-group';

import deliveryChannels from '../constants/delivery-channels';

import scrollbarPositions from './scrollbar-positions';

const messageHighlightDuration = 1500;

const messagesModificationTypes = {
  PREPEND: 'PREPEND',
  APPEND: 'APPEND'
};

class MessagesViewer {

  constructor(configuration) {

    this.conversation = null;
    this.totalMessages = null;
    this.visible = false;
    this.unviewedMessageIds = [];
    this.scrollbarPosition = scrollbarPositions.END;

    this.paginationOptions = this.getDefaultPaginationOptions();
    this.state = this.getDefaultState();
    this.quotedMessagesById = {};
    this.scrollingToMessage = false;

    this.internetPhone = configuration.internetPhone;
    this.conversationId = configuration.conversationId;
    this.serverInterface = configuration.serverInterface;
    this.eventSystem = configuration.eventSystem;
    this.onAllUnviewedMessagesViewed = configuration.onAllUnviewedMessagesViewed;
    this.onNewUnviewedMessage = configuration.onNewUnviewedMessage;
    this.onMessageListUpdate = configuration.onMessageListUpdate || function noop() {};
    this.disableAvatar = configuration.disableAvatar === true;
    this.enableQuotes = configuration.enableQuotes === true;
    this.bookmarksConfiguration = configuration.bookmark;
    this.bookmarkedMessageIds = this.bookmarksConfiguration?.bookmarks?.map(function mapper(bookmark) {
      return bookmark.messageId;
    });

    this.userScrolledUp = false;
    this.lastScrollTop = 0;

    this.scrollObserver = new ScrollObserver({
      actionableZoneFromTop: 50,
      callback: this.handleLoadNextPageEvent.bind(this),
      direction: ScrollObserver.directions.UP,
      onScroll: this.handleScrollEvent.bind(this)
    });

    this.loadingIndicator = el('ht-spinner', { size: 'small' });
    this.messageGroupsDisplay = list('ul#inbox-message-groups.helptap-list.hide-scrollbar', MessageGroup, '_id', {
      ...configuration,
      messagesViewer: this
    });

    this.el = el('#inbox-messages-viewer-main', [this.loadingIndicator, this.messageGroupsDisplay]);

    if (this.disableAvatar === true) {
      this.el.classList.add('avatars-disabled');
    }

  }

  mergeQuotedMessages(quotedMessages) {

    if (Array.isArray(quotedMessages) === false) {
      return;
    }

    for (let quotedMessage of quotedMessages) {
      this.quotedMessagesById[quotedMessage._id] = quotedMessage;
    }

  }

  // A message quoted from the screen is already loaded, so it stands in until a page hydrates it.
  getQuotedMessageById(quotedMessageId) {

    return this.quotedMessagesById[quotedMessageId] || findMessageById(this.state.messageGroups, quotedMessageId);

  }

  canQuoteMessages() {

    if (this.enableQuotes === false) {
      return false;
    }

    if (this.conversation.deliveryChannel !== deliveryChannels.APPLICATION) {
      return false;
    }

    return true;

  }

  quote(message) {

    this.internetPhone.quoteMessage(message);

  }

  async loadMessagesUntilDisplayed(messageId) {

    while (this.isMessageDisplayed(messageId) === false && this.totalMessages > this.paginationOptions.skip) {
      await this.loadMessages();
    }

  }

  highlightMessageElement(messageElement) {

    messageElement.classList.add('highlighted');

    setTimeout(function removeHighlight() {
      messageElement.classList.remove('highlighted');
    }, messageHighlightDuration);

  }

  async scrollToMessage(messageId) {

    if (this.scrollingToMessage === true) {
      return;
    }

    this.scrollingToMessage = true;
    this.scrollObserver.pause();

    try {
      await this.loadMessagesUntilDisplayed(messageId);
      this.bringMessageToView(messageId);
    } finally {
      this.scrollObserver.resume();
      this.scrollingToMessage = false;
    }

  }

  bringMessageToView(messageId) {

    const messageElement = this.messageGroupsDisplay.el.querySelector(`#message-${messageId}`);

    if (messageElement === null) {
      return;
    }

    this.messageGroupsDisplay.el.scrollTo({ top: messageElement.offsetTop - 50, behavior: 'smooth' });

    this.highlightMessageElement(messageElement);

  }

  setBookmarks(bookmarks) {

    this.bookmarksConfiguration.bookmarks = bookmarks;
    this.bookmarkedMessageIds = this.bookmarksConfiguration.bookmarks.map(function mapper(bookmark) {
      return bookmark.messageId;
    });
    this.messagesUpdated();

  }

  async bookmark(messageId) {

    this.bookmarkedMessageIds.push(messageId);

    const bookmark = await this.bookmarksConfiguration.onBookmark(messageId);

    this.bookmarksConfiguration.bookmarks.push(bookmark);

    this.internetPhone.bookmarksChanged();

  }

  async unbookmark(messageId) {

    let bookmarkIndex = this.bookmarkedMessageIds.indexOf(messageId);

    if (bookmarkIndex === -1) {
      return;
    }

    this.bookmarkedMessageIds.splice(bookmarkIndex, 1);

    await this.bookmarksConfiguration.onUnbookmark(messageId);

    bookmarkIndex = this.bookmarksConfiguration.bookmarks.findIndex(function finder(bookmark) {
      return bookmark.messageId === messageId;
    });

    if (bookmarkIndex !== -1) {
      this.bookmarksConfiguration.bookmarks.splice(bookmarkIndex, 1);
    }

    this.internetPhone.bookmarksChanged();

  }

  async onmount() {

    const externalEventCodes = (await import( /* webpackIgnore: true */ 'https://cdn.venturz.co/modules/event-system/codes/external.js')).default;

    this.eventSystem.internal.eventFor(this, externalEventCodes.INBOUND_CONVERSATION_MESSAGE_CREATED, this.handleInboundConversationMessage.bind(this));
    this.eventSystem.internal.eventFor(this, externalEventCodes.OUTBOUND_CONVERSATION_MESSAGE_CREATED, this.handleOutboundConversationMessage.bind(this));
    this.eventSystem.internal.eventFor(this, externalEventCodes.INBOUND_CONVERSATION_MESSAGE_UPDATED, this.handleInboundConversationMessageUpdate.bind(this));
    this.eventSystem.internal.eventFor(this, externalEventCodes.OUTBOUND_CONVERSATION_MESSAGE_UPDATED, this.handleOutboundConversationMessageUpdate.bind(this));
    this.eventSystem.internal.eventFor(this, externalEventCodes.OUTBOUND_CONVERSATION_MESSAGE_DELIVERED, this.handleMessageDeliveredReceipt.bind(this));
    this.eventSystem.internal.eventFor(this, externalEventCodes.OUTBOUND_CONVERSATION_MESSAGE_VIEWED, this.handleMessageViewedReceipt.bind(this));

    this.initialize();

    this.messageGroupsDisplay.el.scrollTop = this.messageGroupsDisplay.el.scrollHeight;

  }

  onunmount() {

    this.eventSystem.internal.offAllOn(this);

  }

  registerScrollObserver() {

    this.messageGroupsDisplay.el.addEventListener('scroll', this.scrollObserver.callback);

  }

  tryBringLatestMessageToView() {

    if (this.messageGroupsDisplay.views.length === 0) {
      return;
    }

    const latestMessageElement = this.getLatestMessageElement();

    if (latestMessageElement === void 0) {
      return;
    }

    setTimeout(() => this.messageGroupsDisplay.el.scrollTo({ top: latestMessageElement.offsetTop - 50, behaviour: 'smooth' }), 10);

  }

  initialize() {

    this.registerScrollObserver();

    this.tryBringLatestMessageToView();

  }

  getDefaultPaginationOptions() {

    return {
      skip: 0,
      limit: 20
    };

  }

  getDefaultState() {

    return {
      loading: false,
      messageGroups: []
    };

  }

  async update(conversation, messages) {

    this.conversation = conversation;
    this.paginationOptions = this.getDefaultPaginationOptions();
    this.state = this.getDefaultState();
    this.quotedMessagesById = {};
    this.unviewedMessageIds = [];

    this.messageGroupsDisplay.update([]);

    this.scrollObserver.pause();

    if (messages === void 0 || messages === null) {
      await this.loadMessages();
    } else {
      this.displayMessages(messages);
    }

    this.scrollObserver.resume();

  }

  // The first page of a conversation the screen opens on arrives already fetched, alongside it.
  displayMessages(messages) {

    const conversation = this.conversation;

    this.totalMessages = messages.total;
    this.paginationOptions.skip += messages.documents.length;

    this.mergeQuotedMessages(messages.quotedMessages);

    this.state.messageGroups = prependMessages(this.state.messageGroups, messages.documents.reverse(), conversation);
    this.loadingMessagesEnded();
    this.messagesPrepended();

  }

  handleInboundConversationMessage(payload) {

    const conversation = payload.conversation;

    if (conversation._id !== this.conversation._id) {
      return;
    }

    this.tryEmitInboundMessageDelivered(payload);

    const message = payload.message;

    if (this.isMessageDisplayed(message._id) === true) {
      this.internetPhone.refreshActivityAndSuggestions(message);
      return;
    }

    appendMessage(this.state.messageGroups, message, this.conversation);

    this.messagesAppended();
    this.tryEmitInboundMessageViewed(payload);
    this.internetPhone.refreshActivityAndSuggestions(message);

  }

  handleInboundConversationMessageUpdate(payload) {

    const conversation = payload.conversation;

    if (conversation._id !== this.conversation._id) {
      return;
    }

    const message = payload.message;

    if (this.isMessageDisplayed(message._id) === false) {
      return;
    }

    var location = updateMessage(this.state.messageGroups, message);

    if (location === false) {
      return;
    }

    this.updateSingleMessage(location);

    this.tryScrollToBottomIfAtEnd();

  }

  appendMessage(message) {

    appendMessage(this.state.messageGroups, message, this.conversation);
    this.messagesAppended();

  }

  removeMessageById(messageId) {

    removeMessage(this.state.messageGroups, messageId);
    this.messagesAppended();

  }

  handleOutboundConversationMessage(payload) {

    const conversation = payload.conversation;

    if (conversation._id !== this.conversation._id) {
      return;
    }

    this.tryEmitOutboundMessageDelivered(payload);

    const message = payload.message;

    if (this.isMessageDisplayed(message._id) === true) {
      return;
    }

    appendMessage(this.state.messageGroups, message, conversation);

    this.messagesAppended();

  }

  handleOutboundConversationMessageUpdate(payload) {

    const conversation = payload.conversation;

    if (conversation._id !== this.conversation._id) {
      return;
    }

    const message = payload.message;

    if (this.isMessageDisplayed(message._id) === false) {
      return;
    }

    var location = updateMessage(this.state.messageGroups, message);

    if (location === false) {
      return;
    }

    this.updateSingleMessage(location);

    this.tryScrollToBottomIfAtEnd();

  }

  displayLoadingIndicator() {

    const classes = ['loading'];

    if (this.state.messageGroups.length === 0) {
      classes.push('empty');
    }

    this.el.classList.add(...classes);

    setAttr(this.loadingIndicator, { style: { display: 'block' } });

  }

  hideLoadingIndicator() {

    this.el.classList.remove('loading', 'empty');

    setAttr(this.loadingIndicator, { style: { display: 'none' } });

  }

  loadingMessagesStarted() {

    this.loadingMessages = true;
    this.displayLoadingIndicator();

  }

  loadingMessagesEnded() {

    this.loadingMessages = false;
    this.hideLoadingIndicator();

  }

  setMessageAsViewed(message) {

    this.serverInterface.setConversationMessageAsViewed(message.conversationId, message._id);

  }

  async loadMessages() {

    const conversation = this.conversation;
    const conversationId = conversation._id;
    const paginationOptions = this.paginationOptions;

    this.loadingMessagesStarted();

    const response = await this.serverInterface.getPaginatedConversationMessagesByConversationIdAndQueryOptions(conversationId, paginationOptions);

    // if user moved to another conversation, skipping ui updates
    if (conversationId !== this.conversation._id) {
      return;
    }

    this.loadingMessagesEnded();

    this.totalMessages = response.messages.total;
    this.paginationOptions.skip += response.messages.documents.length;

    this.mergeQuotedMessages(response.quotedMessages);

    this.state.messageGroups = prependMessages(this.state.messageGroups, response.messages.documents.reverse(), conversation);

    this.messagesPrepended();

  }

  notifyMessageListUpdate() {

    var messages = buildMessageList(this.state.messageGroups);

    this.onMessageListUpdate(messages);

  }

  messagesUpdated() {

    const scrollTop = this.messageGroupsDisplay.el.scrollTop;
    const scrollHeight = this.messageGroupsDisplay.el.scrollHeight;
    const clientHeight = this.messageGroupsDisplay.el.clientHeight;

    this.messageGroupsDisplay.update(this.state.messageGroups);

    this.notifyMessageListUpdate();

    return {
      scrollTop,
      scrollHeight,
      clientHeight
    };

  }

  updateSingleMessage(location) {

    var messageGroup = this.state.messageGroups[location.groupIndex];
    var message = messageGroup.messages[location.messageIndex];
    var groupView = this.messageGroupsDisplay.views[location.groupIndex];
    var messageView = groupView.messagesDisplay.views[location.messageIndex];

    messageView.update(message, location.messageIndex, messageGroup.messages, messageGroup);

    this.notifyMessageListUpdate();

  }

  messagesPrepended() {

    const previousScrollDetails = this.messagesUpdated();

    this.messagesDisplayed(previousScrollDetails, messagesModificationTypes.PREPEND);

  }

  messagesAppended() {

    this.internetPhone.updateRecipientsInput(this.getLatestMessage());

    const previousScrollDetails = this.messagesUpdated();

    this.messagesDisplayed(previousScrollDetails, messagesModificationTypes.APPEND);

  }

  getLatestMessageElement() {

    return this.messageGroupsDisplay.views[this.messageGroupsDisplay.views.length - 1]?.el.querySelector('.inbox-message-group-message-main:last-child');

  }

  tryScrollToBottomIfAtEnd() {

    if (this.userScrolledUp === true) {
      return;
    }

    this.scrollToBottom();

  }

  scrollToBottom() {

    var scrollableContainer = this.messageGroupsDisplay.el;

    scrollableContainer.scrollTo({ top: scrollableContainer.scrollHeight, behavior: 'smooth' });

  }

  tryShowLatestMessage(previousScrollDetails, mode) {

    const scrollTop = previousScrollDetails.scrollTop;
    const scrollHeight = previousScrollDetails.scrollHeight;
    const clientHeight = previousScrollDetails.clientHeight;
    const heightBuffer = 1;
    let newScrollTop = scrollTop;

    if ((scrollTop + clientHeight + heightBuffer) >= scrollHeight) {
      const latestMessageElement = this.getLatestMessageElement();
      if (latestMessageElement === void 0) {
        return;
      }
      newScrollTop = latestMessageElement.offsetTop - 50;
    } else if (mode === messagesModificationTypes.PREPEND) {
      newScrollTop = this.messageGroupsDisplay.el.scrollHeight - scrollHeight;
    } else if (mode === messagesModificationTypes.APPEND) {
      const latestMessageElement = this.getLatestMessageElement();
      if (latestMessageElement === void 0) {
        return;
      }
      newScrollTop = latestMessageElement.offsetTop - 50;
    }

    this.messageGroupsDisplay.el.scrollTo({ top: newScrollTop });

  }

  messagesDisplayed(previousScrollDetails, mode) {

    this.tryShowLatestMessage(previousScrollDetails, mode);
    const updated = this.tryMarkMessagesAsViewed();

    if (updated === true) {
      this.messagesUpdated();
    }

  }

  getLatestMessage() {

    const messageGroups = this.state.messageGroups;

    const messageGroupsLength = messageGroups.length;

    if (messageGroupsLength === 0) {
      return null;
    }

    const messages = messageGroups[messageGroupsLength - 1].messages;
    const message = messages[messages.length - 1];

    return message;

  }

  containerHeightChanged() {

    if (this.scrollbarPosition !== scrollbarPositions.END) {
      return;
    }

    this.messageGroupsDisplay.el.scrollTop = this.messageGroupsDisplay.el.scrollHeight;

  }

  isMessageDisplayed(messageId) {

    return isMessageExistsById(this.state.messageGroups, messageId);

  }

  isExternalMessageDisplayed(messageId) {

    return isExternalMessageExistsById(this.state.messageGroups, messageId);

  }

  getMessageByExternalId(messageId) {

    return findMessageByExternalId(this.state.messageGroups, messageId);

  }

  updateScrollbarPosition() {

    const scrollableContainer = this.messageGroupsDisplay.el;

    if ((scrollableContainer.scrollTop + scrollableContainer.clientHeight) === scrollableContainer.scrollHeight) {
      this.scrollbarPosition = scrollbarPositions.END;
    } else if (this.el.scrollTop === 0) {
      this.scrollbarPosition = scrollbarPositions.TOP;
    } else {
      this.scrollbarPosition = scrollbarPositions.IN_BETWEEN;
    }

  }

  handleScrollEvent() {

    const container = this.messageGroupsDisplay.el;
    const currentScrollTop = container.scrollTop;

    if (currentScrollTop < this.lastScrollTop) {
      this.userScrolledUp = true;
    }

    this.lastScrollTop = currentScrollTop;

    const isAtBottom = (currentScrollTop + container.clientHeight + 1) >= container.scrollHeight;

    if (isAtBottom) {
      this.userScrolledUp = false;
    }

    this.updateScrollbarPosition();

  }

  async handleLoadNextPageEvent() {

    if (this.totalMessages <= this.paginationOptions.skip) {
      return;
    }

    if (this.working === true) {
      return;
    }

    this.working = true;

    await this.loadMessages();

    this.working = false;

  }

  setVisible(visibility) {

    this.visibility = visibility;

    if (this.visibility === true) {
      this.setUnviewedMessagesViewed();
    }

  }

  onMessageDelivered() {

  }

  onMessageViewed(message) {

    this.serverInterface.setConversationMessageAsViewed(message.conversationId, message._id);

  }

  inView() {

    this.visible = true;

    this.setUnviewedMessagesViewed();

  }

  inBackground() {

    this.visible = false;

  }

  setUnviewedMessagesViewed() {

    const conversationId = this.conversation._id;
    const unviewedMessageIds = this.unviewedMessageIds;

    for (let unviewedMessageId of unviewedMessageIds) {

      this.serverInterface.setConversationMessageAsViewed(conversationId, unviewedMessageId);

    }

    this.onAllUnviewedMessagesViewed();

  }

  tryEmitInboundMessageDelivered(payload) {

    if (payload.message.type !== 'INBOUND') {
      return;
    }

    this.onMessageDelivered(payload.message);

  }

  tryEmitOutboundMessageDelivered(payload) {

    if (payload.message.type !== 'INBOUND') {
      return;
    }

    this.onMessageDelivered(payload.message);

  }

  tryEmitInboundMessageViewed(payload) {

    const message = payload.message;

    if (this.visible === false) {
      this.unviewedMessageIds.push(message._id);
      this.onNewUnviewedMessage();
      return;
    }

    this.onMessageViewed(message);

  }

  tryMarkMessagesAsViewed() {

    const messageGroups = this.state.messageGroups;
    const messageGroupsLength = messageGroups.length;
    const nativeMessageType = 'OUTBOUND';

    let updated = false;

    for (let index = messageGroupsLength - 1; index >= 0; index--) {

      const messageGroup = messageGroups[index];

      if (nativeMessageType === messageGroup.type) {
        continue;
      }

      const messages = messageGroup.messages;
      const messagesLength = messages.length;

      for (let messageIndex = messagesLength - 1; messageIndex >= 0; messageIndex--) {

        const message = messages[messageIndex];

        if (message.viewedAt !== null) {
          continue;
        }

        updated = true;

        this.onMessageViewed(message);

        message.viewedAt = Date.now();

      }

    }

    return updated;

  }

  handleMessageDeliveredReceipt(payload) {

    const conversation = payload.conversation;

    if (conversation._id !== this.conversation._id) {
      return;
    }

    const message = payload.message;

    setMessageAsDelivered(conversation, message, this.state.messageGroups);

    this.messagesUpdated();

  }

  handleMessageViewedReceipt(payload) {

    const conversation = payload.conversation;

    if (conversation._id !== this.conversation._id) {
      return;
    }

    const message = payload.message;

    setMessageAsViewed(conversation, message, this.state.messageGroups);

    this.messagesUpdated();

  }

}

export default MessagesViewer;
